<template><div><component-combinator component-name="DtAvatar" /><h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">An avatar is a visual representation of a contact, user, or entity in Dialpad.</p>
<p class="d-docsite--paragraph">The component prioritizes different sources for content display, sequentially checking first for an image source (<code v-pre>image-src</code>) or content in the icon slot. If neither are provided, initials are extracted from the full name (<code v-pre>full-name</code>) using the following logic:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">If the string contains two or more words, the result will be the first character of the first and last word capitalized. E.g.:
<code v-pre>full-name: &quot;Jaqueline Nackos&quot;</code> will result in: <code v-pre>JN</code>.</li>
<li class="d-docsite--list-element">If the string contains only one word, the result will be the first two characters capitalized. E.g:
<code v-pre>full-name: &quot;Jaqueline&quot;</code> will result in: <code v-pre>JA</code>.
<code v-pre>full-name: &quot;10&quot;</code> will result in: <code v-pre>10</code>.</li>
<li class="d-docsite--list-element">If full-name is not provided, the result will be an empty string.</li>
</ul>
<table class="d-table dialtone-doc-table d-mbe-200">
    <colgroup>
        <col class="d-w-100">
        <col>
        <col>
        <col>
    </colgroup>
    <thead>
        <tr>
            <th colspan="2">Type</th>
            <th>Usage</th>
        </tr>
    </thead>
    <tbody>
        <tr>
            <td>
                <dt-avatar>
                  <template #icon>
                    <dt-icon-user />
                  </template>
                </dt-avatar>
            </td>
            <th class="d-ta-left"><a class="d-link" href="#icon">Icon</a></th>
            <td>When no username can be associated with the Avatar.</td>
        </tr>
        <tr>
            <td>
                <dt-avatar full-name="Daniel Parker" seed="user-456" />
            </td>
            <th class="d-ta-left"><a class="d-link" href="#initials">Initials</a></th>
            <td>When the user's name is known.</td>
        </tr>
        <tr>
            <td>
                <dt-avatar image-alt="user avatar" image-src="/assets/images/person.png" />
            </td>
            <th class="d-ta-left"><a class="d-link" href="#image">Image</a></th>
            <td>When a custom image has been uploaded.</td>
        </tr>
        <tr>
            <td>
                <dt-avatar image-src="/assets/images/person.png" image-alt="person avatar" :group="3" />
            </td>
            <th class="d-ta-left"><a class="d-link" href="#group">Group</a></th>
            <td>When reflecting more than 2 participants.</td>
        </tr>
    </tbody>
</table>
<h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="icon" tabindex="-1" class="d-docsite--header-3">Icon <a class="header-anchor d-link d-docsite--link d-link" href="#icon" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-avatar&gt;
  &lt;template #icon&gt;
    &lt;dt-icon-user /&gt;
  &lt;/template&gt;
&lt;/dt-avatar&gt;'>
<dt-avatar>
  <template #icon>
    <dt-icon-user />
  </template>
</dt-avatar>
</code-example>
<h3 id="initials" tabindex="-1" class="d-docsite--header-3">Initials <a class="header-anchor d-link d-docsite--link d-link" href="#initials" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Unless otherwise specified via the <code v-pre>color</code> prop, a background color will be provided based on the <code v-pre>seed</code> prop. This background is based on a hashed version of the user ID, allowing the colors to be consistent across sessions. Colors are dynamically computed using OKLCH and adapt to the current theme.</p>
<code-example source-code='&lt;!-- Use seed for consistent random colors per user --&gt;
&lt;dt-avatar
  full-name=&quot;Daniel Parker&quot;
  seed=&quot;user-unique-id&quot;
/&gt;'>
<dt-stack direction="row" gap="200" class="d-w-500 d-fw-wrap">
  <dt-avatar v-for="seed in seeds" :seed="seed" full-name="Daniel Parker" />
</dt-stack>
</code-example>
<h3 id="image" tabindex="-1" class="d-docsite--header-3">Image <a class="header-anchor d-link d-docsite--link d-link" href="#image" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">If <code v-pre>image-src</code> is not provided, or if image fails to load, the avatar will fall back to the initials extracted from the <code v-pre>full-name</code>.</p>
<code-example source-code='&lt;dt-avatar image-src=&quot;/assets/images/person.png&quot; full-name=&quot;Daniel Parker&quot; image-alt=&quot;avatar user&quot; /&gt;
&lt;dt-avatar image-src=&quot;/assets/images/broken-image.png&quot; full-name=&quot;Daniel Parker&quot; image-alt=&quot;avatar user&quot; /&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-avatar image-src="/assets/images/person.png" full-name="Daniel Parker" image-alt="avatar user" />
  <dt-avatar image-src="/assets/images/broken-image.png" full-name="Daniel Parker" image-alt="avatar user" />
</dt-stack>
</code-example>
<h3 id="sizes" tabindex="-1" class="d-docsite--header-3">Sizes <a class="header-anchor d-link d-docsite--link d-link" href="#sizes" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Avatar supports a 100-based sizing scale. T-shirt sizes (<code v-pre>xs</code>, <code v-pre>sm</code>, <code v-pre>md</code>, <code v-pre>lg</code>, <code v-pre>xl</code>) are deprecated but still supported as aliases.</p>
<table>
<thead>
<tr>
<th>Size</th>
<th>Alias</th>
<th>Dimensions</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>100</code></td>
<td><code v-pre>xs</code></td>
<td>16px</td>
</tr>
<tr>
<td><code v-pre>150</code></td>
<td>—</td>
<td>20px</td>
</tr>
<tr>
<td><code v-pre>200</code></td>
<td><code v-pre>sm</code></td>
<td>24px</td>
</tr>
<tr>
<td><code v-pre>250</code></td>
<td>—</td>
<td>28px</td>
</tr>
<tr>
<td><code v-pre>300</code></td>
<td><code v-pre>md</code></td>
<td>32px (default)</td>
</tr>
<tr>
<td><code v-pre>400</code></td>
<td>—</td>
<td>40px</td>
</tr>
<tr>
<td><code v-pre>500</code></td>
<td><code v-pre>lg</code></td>
<td>48px</td>
</tr>
<tr>
<td><code v-pre>600</code></td>
<td><code v-pre>xl</code></td>
<td>64px</td>
</tr>
<tr>
<td><code v-pre>700</code></td>
<td>—</td>
<td>96px</td>
</tr>
<tr>
<td><code v-pre>800</code></td>
<td>—</td>
<td>128px</td>
</tr>
<tr>
<td><code v-pre>900</code></td>
<td>—</td>
<td>256px</td>
</tr>
</tbody>
</table>
<code-example source-code='&lt;dt-avatar size=&quot;{size}&quot;&gt;
  &lt;template #icon&gt;
    &lt;dt-icon-user /&gt;
  &lt;/template&gt;
&lt;/dt-avatar&gt;'>
<dt-stack direction="row" align="center" justify="center" gap="200" class="d-fw-wrap">
  <dt-avatar v-for="size in sizes" :size="size">
    <template #icon>
      <dt-icon-user />
    </template>
  </dt-avatar>
</dt-stack>
</code-example>
<h3 id="group" tabindex="-1" class="d-docsite--header-3">Group <a class="header-anchor d-link d-docsite--link d-link" href="#group" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The group avatar is used to represent group discussions in a compact form. A count badge is added on top of the avatar. The avatar shown is the last person to send a message in the group. The group avatar is available only from sizes 100-500. At size 100, only the count badge is shown.</p>
<code-example source-code='&lt;dt-avatar size=&quot;100&quot; :group=&quot;3&quot; image-src=&quot;/assets/images/person.png&quot; image-alt=&quot;Person Avatar&quot; /&gt;
&lt;dt-avatar size=&quot;150&quot; :group=&quot;5&quot; image-src=&quot;/assets/images/person.png&quot; image-alt=&quot;Person Avatar&quot; /&gt;
&lt;dt-avatar size=&quot;200&quot; :group=&quot;12&quot; image-src=&quot;/assets/images/person.png&quot; image-alt=&quot;Person Avatar&quot; /&gt;
&lt;dt-avatar size=&quot;250&quot; :group=&quot;8&quot; image-src=&quot;/assets/images/person.png&quot; image-alt=&quot;Person Avatar&quot; /&gt;
&lt;dt-avatar size=&quot;300&quot; :group=&quot;24&quot; image-src=&quot;/assets/images/person.png&quot; image-alt=&quot;Person Avatar&quot; /&gt;
&lt;dt-avatar size=&quot;400&quot; :group=&quot;100&quot; image-src=&quot;/assets/images/person.png&quot; image-alt=&quot;Person Avatar&quot; /&gt;
&lt;dt-avatar size=&quot;500&quot; :group=&quot;7&quot; image-src=&quot;/assets/images/person.png&quot; image-alt=&quot;Person Avatar&quot; /&gt;'>
<dt-stack data-demo-wrapper direction="row" align="center" gap="200">
  <dt-avatar size="100" :group="3" image-src="/assets/images/person.png" image-alt="Person Avatar" />
  <dt-avatar size="150" :group="5" image-src="/assets/images/person.png" image-alt="Person Avatar" />
  <dt-avatar size="200" :group="12" image-src="/assets/images/person.png" image-alt="Person Avatar" />
  <dt-avatar size="250" :group="8" image-src="/assets/images/person.png" image-alt="Person Avatar" />
  <dt-avatar size="300" :group="24" image-src="/assets/images/person.png" image-alt="Person Avatar" />
  <dt-avatar size="400" :group="100" image-src="/assets/images/person.png" image-alt="Person Avatar" />
  <dt-avatar size="500" :group="7" image-src="/assets/images/person.png" image-alt="Person Avatar" />
</dt-stack>
</code-example>
<h3 id="presence" tabindex="-1" class="d-docsite--header-3">Presence <a class="header-anchor d-link d-docsite--link d-link" href="#presence" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Provides the user's current <RouteLink to="/components/presence.html" class="d-docsite--link d-link">presence</RouteLink>, positioned in the bottom right corner.</p>
<code-example source-code='&lt;dt-stack direction=&quot;row&quot; align=&quot;center&quot; gap=&quot;100&quot;&gt;
  &lt;dt-avatar :size=&quot;100&quot; presence=&quot;active&quot; image-src=&quot;/assets/images/person.png&quot; image-alt=&quot;Person Avatar&quot; /&gt;
  &lt;dt-avatar :size=&quot;200&quot; presence=&quot;away&quot; image-src=&quot;/assets/images/person.png&quot; image-alt=&quot;Person Avatar&quot; /&gt;
  &lt;dt-avatar :size=&quot;300&quot; presence=&quot;busy&quot; image-src=&quot;/assets/images/person.png&quot; image-alt=&quot;Person Avatar&quot; /&gt;
  &lt;dt-avatar :size=&quot;400&quot; presence=&quot;offline&quot; image-src=&quot;/assets/images/person.png&quot; image-alt=&quot;Person Avatar&quot; /&gt;
  &lt;dt-avatar :size=&quot;500&quot; presence=&quot;active&quot; image-src=&quot;/assets/images/person.png&quot; image-alt=&quot;Person Avatar&quot; /&gt;
&lt;/dt-stack&gt;
&lt;dt-stack direction=&quot;row&quot; align=&quot;center&quot; gap=&quot;100&quot;&gt;
  &lt;dt-avatar :size=&quot;100&quot; presence=&quot;active&quot; seed=&quot;user-1&quot; full-name=&quot;Test Name&quot; /&gt;
  &lt;dt-avatar :size=&quot;200&quot; presence=&quot;away&quot; seed=&quot;user-2&quot; full-name=&quot;William Steele&quot; /&gt;
  &lt;dt-avatar :size=&quot;300&quot; presence=&quot;busy&quot; seed=&quot;user-3&quot; full-name=&quot;Frank Richard&quot; /&gt;
  &lt;dt-avatar :size=&quot;400&quot; presence=&quot;offline&quot; seed=&quot;user-4&quot; full-name=&quot;John Hawkins&quot; /&gt;
  &lt;dt-avatar :size=&quot;500&quot; presence=&quot;active&quot; seed=&quot;user-5&quot; full-name=&quot;Alice Edwards&quot; /&gt;
&lt;/dt-stack&gt;'>
<dt-stack data-demo-wrapper gap="100">
  <dt-stack direction="row" align="center" gap="100">
    <dt-avatar :size="100" presence="active" image-src="/assets/images/person.png" image-alt="Person Avatar" />
    <dt-avatar :size="200" presence="away" image-src="/assets/images/person.png" image-alt="Person Avatar" />
    <dt-avatar :size="300" presence="busy" image-src="/assets/images/person.png" image-alt="Person Avatar" />
    <dt-avatar :size="400" presence="offline" image-src="/assets/images/person.png" image-alt="Person Avatar" />
    <dt-avatar :size="500" presence="active" image-src="/assets/images/person.png" image-alt="Person Avatar" />
  </dt-stack>
  <dt-stack direction="row" align="center" gap="100">
    <dt-avatar :size="100" presence="active" seed="user-1" full-name="Test Name" />
    <dt-avatar :size="200" presence="away" seed="user-2" full-name="William Steele" />
    <dt-avatar :size="300" presence="busy" seed="user-3" full-name="Frank Richard" />
    <dt-avatar :size="400" presence="offline" seed="user-4" full-name="John Hawkins" />
    <dt-avatar :size="500" presence="active" seed="user-5" full-name="Alice Edwards" />
  </dt-stack>
</dt-stack>
</code-example>
<h3 id="overlay" tabindex="-1" class="d-docsite--header-3">Overlay <a class="header-anchor d-link d-docsite--link d-link" href="#overlay" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-avatar :size=&quot;400&quot; image-src=&quot;/assets/images/person.png&quot; image-alt=&quot;avatar user&quot;&gt;
  &lt;template #overlayIcon&gt;
    &lt;dt-icon-hear /&gt;
  &lt;/template&gt;
&lt;/dt-avatar&gt;
&lt;dt-avatar :size=&quot;400&quot; image-src=&quot;/assets/images/person.png&quot; image-alt=&quot;avatar user&quot; overlay-text=&quot;+3&quot; /&gt;'>
<dt-stack data-demo-wrapper direction="row" align="center" gap="200">
  <dt-avatar :size="400" image-src="/assets/images/person.png" image-alt="avatar user">
    <template #overlayIcon>
      <dt-icon-hear />
    </template>
  </dt-avatar>
  <dt-avatar :size="400" image-src="/assets/images/person.png" image-alt="avatar user" overlay-text="+3" />
</dt-stack>
</code-example>
<h3 id="interactive" tabindex="-1" class="d-docsite--header-3">Interactive <a class="header-anchor d-link d-docsite--link d-link" href="#interactive" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Avatars that appear alongside a visible label (e.g., a user's name) are decorative and should not be focusable or announced by screen readers. This is the default behavior.</p>
<p class="d-docsite--paragraph">Avatars that convey meaning on their own — such as navigation or actions — should be made interactive using the <code v-pre>interactive</code> prop. This renders the avatar as a <code v-pre>&lt;button&gt;</code> with visible focus ring and keyboard activation via Enter and Space. Provide an accessible name via <code v-pre>icon-aria-label</code> (for icon avatars), <code v-pre>full-name</code> (for initials avatars), or <code v-pre>image-alt</code> (for image avatars).</p>
<code-example source-code='&lt;dt-avatar interactive icon-aria-label=&quot;user&quot;&gt;
  &lt;template #icon&gt;
    &lt;dt-icon-user /&gt;
  &lt;/template&gt;
&lt;/dt-avatar&gt;'>
<dt-avatar interactive icon-aria-label="user">
  <template #icon>
    <dt-icon-user />
  </template>
</dt-avatar>
</code-example>
<h3 id="deactivated" tabindex="-1" class="d-docsite--header-3">Deactivated <a class="header-anchor d-link d-docsite--link d-link" href="#deactivated" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use the <code v-pre>deactivated</code> prop to render the avatar in a desaturated/washed-out state. This is useful to indicate that a user is deactivated or inactive.</p>
<code-example source-code='&lt;dt-avatar deactivated image-src=&quot;/assets/images/person.png&quot; image-alt=&quot;Deactivated user&quot; /&gt;
&lt;dt-avatar deactivated full-name=&quot;Deactivated User&quot; seed=&quot;user-deactivated&quot; /&gt;
&lt;dt-avatar deactivated&gt;
  &lt;template #icon&gt;
    &lt;dt-icon-user /&gt;
  &lt;/template&gt;
&lt;/dt-avatar&gt;'>
<dt-stack data-demo-wrapper direction="row" align="center" gap="200">
  <dt-avatar deactivated image-src="/assets/images/person.png" image-alt="Deactivated user" />
  <dt-avatar deactivated full-name="Deactivated User" seed="user-deactivated" />
  <dt-avatar deactivated>
    <template #icon>
      <dt-icon-user />
    </template>
  </dt-avatar>
</dt-stack>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="avatar" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="avatar"></component-class-table><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Initials avatars use background and text color pairings that meet WCAG AA minimum contrast requirements. Variants 0–5 use light text on dark backgrounds, and variants 6–9 use dark text on light backgrounds.</p>
<p class="d-docsite--paragraph">The <code v-pre>deactivated</code> prop applies a visual desaturation effect that is not conveyed to screen readers. If the deactivated status is meaningful, communicate it through surrounding text or an <code v-pre>aria-label</code> on a parent element.</p>
<p class="d-docsite--paragraph">The group count badge includes <code v-pre>role=&quot;img&quot;</code> and an <code v-pre>aria-label</code> (e.g., &quot;3 participants&quot;) so screen readers announce the participant count with context rather than reading the raw number.</p>
<p class="d-docsite--paragraph">For more guidance, see the <a href="https://www.w3.org/WAI/tutorials/images/decorative/" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">WCAG images tutorial</a>.</p>
</div></template>


<script setup>
import { DtIconUser, DtIconHear } from '@dialpad/dialtone-icons/vue';

const seeds = ['alice', 'bob', 'carol', 'david', 'eve', 'frank', 'grace', 'henry', 'iris', 'jack', 'kate', 'leo', 'mia', 'noah', 'olive', 'paul', 'quinn', 'ruby'];
const sizes = ['100', '150', '200', '250', '300', '400', '500', '600', '700', '800', '900'];
</script>